import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigationType } from 'react-router-dom';

import { ApiError } from '../../api/client';
import { foldStartsOpen, rememberFold } from '../../components/ui/filterFold';
import { FilterIcon, PlusIcon } from '../../components/ui/icons';
import { useDetailInView } from '../../components/ui/useDetailInView';
import { useAuth } from '../auth/AuthContext';
import { ARCHIVED_FILTERS, type ArchivedFilter } from './api';
import type { Focus } from '../layout/focus';
import { useSelectedId } from '../layout/useSelectedId';
import { ComponentForm } from './ComponentForm';
import { ComponentPanel } from './ComponentPanel';
import { DuplicateClusters } from './DuplicateClusters';
import { useLabel } from './labels';
import { readComponentSearch } from './searchState';
import { useCatalogueScroll } from './useCatalogueScroll';
import { useComponentSearch } from './useComponentSearch';
import {
  getType,
  listPackages,
  listTypes,
  searchComponents,
  SEARCH_SORTS,
  type SearchParams,
  type SearchSort,
} from './api';

const FILTERS_KEY = 'storageapp.components.filters';

/**
 * The catalogue.
 *
 * One search box rather than a mode switch, because someone hunting a part
 * knows what they remember — a part number, a description, a value — but not
 * which kind of search that counts as. Filters narrow the same query instead of
 * replacing it.
 */
export function ComponentsPage({ focus }: { focus?: Focus | null }) {
  const { t } = useTranslation();
  const { pick } = useLabel();
  const { can } = useAuth();

  const [search, updateSearch] = useComponentSearch();
  const navigationType = useNavigationType();
  const { query, typeId, packageId, valueMin, valueMax, inStock, archived, sort, attributes } =
    readComponentSearch(search);
  const listSearch = new URLSearchParams(search);
  listSearch.delete('serial');
  const [advanced, setAdvanced] = useState(false);
  const [selectedId, setSelectedId] = useSelectedId('/components', listSearch.toString());
  const [showFilters, setShowFilters] = useState(() => foldStartsOpen(FILTERS_KEY));
  const [creating, setCreating] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  // Whether an open form has unsaved changes. Clicking another result switches
  // to it straight away when nothing was touched, which is the usual case —
  // having to press Cancel first was an extra click for nothing.
  const [dirty, setDirty] = useState(false);

  const detail = useDetailInView(`${selectedId}/${creating}/${cleaning}`);

  const open = (id: string) => {
    if (dirty && !window.confirm(t('components.discardChanges'))) return;
    setDirty(false);
    setSelectedId(id);
    setCreating(false);
    setCleaning(false);
  };

  // Which record is shown is the address's business now; what is left here is
  // getting out of the way of it. Deliberately bypasses the unsaved-changes
  // prompt in `open`: the user is holding the part, and a confirm dialog on
  // arrival from a camera would be answered blind.
  useEffect(() => {
    if (!focus || navigationType === 'REPLACE') return;
    setCreating(false);
    setCleaning(false);
    setDirty(false);
  }, [focus, navigationType]);

  const canEdit = can('catalog.write');
  const canMerge = can('component.merge');

  // Only types that occur in stored components — same reasoning as packages: a
  // filter offering a type nothing is stored under returns nothing.
  const types = useQuery({
    queryKey: ['reference', 'types', 'used'],
    queryFn: () => listTypes('used'),
  });
  // Only packages that occur in stored components: a filter offering a package
  // nothing is stored in is a filter that returns nothing.
  const packages = useQuery({
    queryKey: ['reference', 'packages', 'used'],
    queryFn: () => listPackages('used'),
  });

  // The parameters of the chosen type. Without a type there is nothing to
  // offer: "±1%" is a tolerance on a resistor and nothing at all on a connector.
  const typeDetail = useQuery({
    queryKey: ['reference', 'type', typeId],
    queryFn: () => getType(typeId),
    enabled: Boolean(typeId),
  });

  const params: SearchParams = {
    q: query || undefined,
    component_type_id: typeId || null,
    package_id: packageId || null,
    // A range without a type has nothing to interpret it: "4k" is ohms or
    // henries depending on what the part is, and the server refuses to guess.
    value_min: typeId ? valueMin || undefined : undefined,
    value_max: typeId ? valueMax || undefined : undefined,
    in_stock: inStock,
    archived,
    attributes: Object.fromEntries(
      Object.entries(attributes).filter(([, value]) => value !== ''),
    ),
    sort,
  };

  const results = useQuery({
    queryKey: ['components', 'search', params],
    queryFn: () => searchComponents(params),
  });

  const selectedType = types.data?.find((row) => row.id === typeId);

  /* How much is narrowing the list, shown on the shut fold. The search box is
     not counted: it stays on screen, so it can never be the hidden reason. */
  const narrowing =
    (typeId ? 1 : 0) +
    (packageId ? 1 : 0) +
    (valueMin.trim() || valueMax.trim() ? 1 : 0) +
    (inStock ? 1 : 0) +
    (archived === 'hide' ? 0 : 1) +
    Object.values(attributes).filter((value) => value !== '').length;

  // On a phone the two columns do not fit side by side, so the pane showing
  // something wins and the list steps aside. Without this the detail column was
  // squeezed to nothing and selecting a component appeared to do nothing at all.
  const showingDetail = Boolean(selectedId || creating || cleaning);
  const scroll = useCatalogueScroll(showingDetail, listSearch.toString(), results.isSuccess);
  const resetSearch = () => updateSearch(readComponentSearch(new URLSearchParams()));
  const activeFilters: { key: string; label: string; remove: () => void }[] = [];
  if (query) activeFilters.push({ key: 'q', label: `${t('common.search')}: ${query}`, remove: () => updateSearch({ query: '' }) });
  if (typeId) activeFilters.push({
    key: 'type',
    label: `${t('components.type')}: ${selectedType ? pick(selectedType.name_ru, selectedType.name_en) : typeId}`,
    remove: () => updateSearch({ typeId: '' }),
  });
  if (packageId) activeFilters.push({
    key: 'package',
    label: `${t('components.package')}: ${packages.data?.find((row) => row.id === packageId)?.code ?? packageId}`,
    remove: () => updateSearch({ packageId: '' }),
  });
  if (valueMin || valueMax) activeFilters.push({
    key: 'range',
    label: `${t('components.value')}: ${valueMin || '…'} — ${valueMax || '…'}`,
    remove: () => updateSearch({ valueMin: '', valueMax: '' }),
  });
  if (inStock) activeFilters.push({ key: 'stock', label: t('components.inStock'), remove: () => updateSearch({ inStock: false }) });
  if (archived !== 'hide') activeFilters.push({ key: 'archive', label: t(`common.archivedFilter.${archived}`), remove: () => updateSearch({ archived: 'hide' }) });
  for (const [key, value] of Object.entries(attributes)) {
    const spec = typeDetail.data?.attributes.find((item) => item.key === key);
    const option = spec?.enum_values?.find((item) => item.value === value);
    activeFilters.push({
      key: `attr:${key}`,
      label: `${spec ? pick(spec.label_ru, spec.label_en) : key}: ${option ? pick(option.label_ru, option.label_en) : value}`,
      remove: () => updateSearch({ attributes: Object.fromEntries(Object.entries(attributes).filter(([name]) => name !== key)) }),
    });
  }
  const hasFilters = activeFilters.length > 0;
  if (sort !== 'relevance') activeFilters.push({ key: 'sort', label: `${t('components.sortBy')}: ${t(`components.sort.${sort}`)}`, remove: () => updateSearch({ sort: 'relevance' }) });

  return (
    <div className={`components${showingDetail ? ' is-detail' : ''}`}>
      <aside className="components__list">
        <div className="components__search">
          <input
            value={query}
            onChange={(event) => updateSearch({ query: event.target.value })}
            placeholder={t('components.searchHint')}
            aria-label={t('common.search')}
          />
          {canEdit && (
            <button
              type="button"
              className="components__icon"
              title={t('components.add')}
              aria-label={t('components.add')}
              onClick={() => {
                if (dirty && !window.confirm(t('components.discardChanges'))) return;
                setDirty(false);
                setCreating(true);
                setCleaning(false);
                setSelectedId(null);
              }}
            >
              <PlusIcon />
            </button>
          )}
          {/* What is narrowing the list, and the way to get at it. Shut on a
              phone, where six controls one per row take half the screen and the
              screen is for the parts. */}
          <button
            type="button"
            className="components__filter-toggle"
            aria-expanded={showFilters}
            title={t('common.filters')}
            aria-label={t('common.filters')}
            aria-controls="component-filters"
            onClick={() => {
              const next = !showFilters;
              setShowFilters(next);
              rememberFold(FILTERS_KEY, next);
            }}
          >
            <FilterIcon />
            {narrowing > 0 && <span className="tabs__count">{narrowing}</span>}
          </button>
          {canMerge && (
            <button
              type="button"
              className={cleaning ? 'is-active' : undefined}
              onClick={() => {
                if (dirty && !window.confirm(t('components.discardChanges'))) return;
                setDirty(false);
                setCleaning((value) => !value);
                setCreating(false);
              }}
              title={t('components.clusters.title')}
            >
              ⧉
            </button>
          )}
        </div>

        {activeFilters.length > 0 && (
          <div className="components__active-filters" role="group" aria-label={t('components.activeFilters')}>
            {activeFilters.map((filter) => (
              <button
                type="button"
                key={filter.key}
                className="components__filter-chip"
                onClick={filter.remove}
                aria-label={t('components.removeFilter', { filter: filter.label })}
              >
                <span>{filter.label}</span><span aria-hidden="true">×</span>
              </button>
            ))}
            <button type="button" className="components__reset" onClick={resetSearch}>
              {t('components.resetSearch')}
            </button>
          </div>
        )}

        <div id="component-filters" className="components__filters" hidden={!showFilters}>
          <select
            aria-label={t('components.type')}
            value={typeId}
            onChange={(event) => {
              updateSearch({ typeId: event.target.value });
            }}
          >
            <option value="">{t('components.anyType')}</option>
            {(types.data ?? []).map((row) => (
              <option key={row.id} value={row.id}>
                {pick(row.name_ru, row.name_en)}
              </option>
            ))}
          </select>

          <select aria-label={t('components.package')} value={packageId} onChange={(event) => updateSearch({ packageId: event.target.value })}>
            <option value="">{t('components.anyPackage')}</option>
            {(packages.data ?? []).map((row) => (
              <option key={row.id} value={row.id}>
                {row.code}
              </option>
            ))}
          </select>

          {/* Only offered once a type is chosen, since that is what says what
              the numbers measure. */}
          {selectedType?.value_kind && (
            <span className="components__range">
              <input
                value={valueMin}
                onChange={(event) => updateSearch({ valueMin: event.target.value })}
                aria-label={t('components.minimumValue')}
                placeholder={t('components.valueFrom')}
                size={6}
              />
              <input
                value={valueMax}
                onChange={(event) => updateSearch({ valueMax: event.target.value })}
                aria-label={t('components.maximumValue')}
                placeholder={t('components.valueTo')}
                size={6}
              />
            </span>
          )}

          <label className="components__instock">
            <input
              type="checkbox"
              checked={inStock}
              onChange={(event) => updateSearch({ inStock: event.target.checked })}
            />
            {t('components.inStock')}
          </label>

          {/* Three answers, not two. Hidden by default — an archived part is
              one somebody decided is no longer stocked. "Only" is the one that
              makes the archive a place to look through: mixed into the live
              catalogue, a handful of archived rows are needles in a haystack. */}
          <select
            aria-label={t('components.archiveFilter')}
            value={archived}
            onChange={(event) => updateSearch({ archived: event.target.value as ArchivedFilter })}
          >
            {ARCHIVED_FILTERS.map((value) => (
              <option key={value} value={value}>
                {t(`common.archivedFilter.${value}`)}
              </option>
            ))}
          </select>

          <select
            value={sort}
            onChange={(event) => updateSearch({ sort: event.target.value as SearchSort })}
            title={t('components.sortBy')}
            aria-label={t('components.sortBy')}
          >
            {SEARCH_SORTS.map((value) => (
              <option key={value} value={value}>
                {t(`components.sort.${value}`)}
              </option>
            ))}
          </select>

          {/* Offered only with a type chosen, because that is what says which
              parameters exist and what their values mean. */}
          {typeId && (typeDetail.data?.attributes.length ?? 0) > 0 && (
            <button
              type="button"
              className="component-form__scope"
              onClick={() => setAdvanced(!advanced)}
            >
              {advanced ? t('components.advancedHide') : t('components.advanced')}
            </button>
          )}
        </div>

        {showFilters && advanced && typeId && (
          <div className="components__advanced">
            {(typeDetail.data?.attributes ?? []).map((spec) => (
              <label key={spec.key}>
                {pick(spec.label_ru, spec.label_en)}
                {spec.enum_values ? (
                  <select
                    value={attributes[spec.key] ?? ''}
                    onChange={(event) =>
                      updateSearch({ attributes: { ...attributes, [spec.key]: event.target.value } })
                    }
                  >
                    <option value="">—</option>
                    {spec.enum_values.map((option) => (
                      <option key={option.value} value={option.value}>
                        {pick(option.label_ru, option.label_en)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    value={attributes[spec.key] ?? ''}
                    // Stored values are normalised on the way in, so a filter
                    // typed with a comma has to be normalised the same way or it
                    // matches nothing.
                    onChange={(event) =>
                      updateSearch({ attributes: {
                        ...attributes,
                        [spec.key]:
                          spec.data_type === 'number'
                            ? event.target.value.replace(',', '.')
                            : event.target.value,
                      } })
                    }
                  />
                )}
              </label>
            ))}
          </div>
        )}

        {results.isFetching && <p className="panel__hint" role="status">{t('common.loading')}</p>}
        {results.isError && (
          <div className="components__feedback" role="alert">
            <strong>{t('components.searchFailed')}</strong>
            <p>{t(results.error instanceof ApiError && results.error.code === 'network'
              ? 'components.searchNetworkError' : 'components.searchErrorHint')}</p>
            <button type="button" onClick={() => void results.refetch()} disabled={results.isFetching}>
              {t('components.retrySearch')}
            </button>
          </div>
        )}
        {results.isSuccess && !results.isFetching && results.data.length === 0 && (
          <div className="components__feedback" role="status">
            <strong>{t(hasFilters ? 'components.empty' : 'components.catalogueEmpty')}</strong>
            <p>{t(hasFilters ? 'components.noMatchesHint' : 'components.catalogueEmptyHint')}</p>
            {hasFilters && <button type="button" onClick={resetSearch}>{t('components.resetSearch')}</button>}
          </div>
        )}

        <ul className="components__results" ref={scroll.list} aria-busy={results.isFetching}>
          {(results.data ?? []).map(({ component }) => (
            <li key={component.id}>
              <button
                type="button"
                className={
                  [
                    component.id === selectedId ? 'is-selected' : '',
                    // Faded and struck through: a row that looks like any other
                    // is a row somebody will try to order from, and the refusal
                    // that follows will read as a fault.
                    component.archived_at ? 'is-archived' : '',
                  ]
                    .filter(Boolean)
                    .join(' ') || undefined
                }
                onClick={(event) => {
                  scroll.selected.current = event.currentTarget;
                  open(component.id);
                }}
              >
                <span className="components__name">{component.name}</span>
                <span className="components__meta">
                  {component.archived_at && (
                    <span className="badge badge--warn">{t('common.archived')}</span>
                  )}
                  {component.mpn && <em>{component.mpn}</em>}
                  <span className="components__code">{component.internal_code}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <div className="components__detail" ref={detail}>
        <button
          type="button"
          className="pane-back"
          onClick={() => {
            if (dirty && !window.confirm(t('components.discardChanges'))) return;
            setDirty(false);
            setSelectedId(null);
            setCreating(false);
            setCleaning(false);
          }}
        >
          ← {t('common.backToList')}
        </button>

        {cleaning ? (
          <DuplicateClusters
            onOpen={(id) => {
              setCleaning(false);
              setSelectedId(id);
            }}
          />
        ) : creating ? (
          <ComponentForm
            onSaved={(saved) => {
              setDirty(false);
              setCreating(false);
              setSelectedId(saved.id);
            }}
            onCancel={() => {
              setDirty(false);
              setCreating(false);
            }}
            onOpenExisting={open}
            onDirtyChange={setDirty}
          />
        ) : selectedId ? (
          <ComponentPanel
            // Remounts when the selection changes, so an open edit form does not
            // carry the previous component's values into the new one.
            key={selectedId}
            componentId={selectedId}
            onArchived={() => {
              setDirty(false);
              setSelectedId(null);
            }}
            onOpenExisting={open}
            onDirtyChange={setDirty}
          />
        ) : (
          <p className="panel__hint">{t('components.selectHint')}</p>
        )}
      </div>
    </div>
  );
}
